import React from 'react';
import { AIAnalysisResult } from '../types';
import { DownloadIcon, UploadIcon, PingIcon, SparklesIcon, ShieldCheckIcon, ShieldExclamationIcon, InfoIcon } from 'shared-icons';

const ratingMap: { [key: string]: { color: string; Icon: React.FC<any> } } = {
  Excellent: { color: 'text-green-500 dark:text-green-400', Icon: ShieldCheckIcon },
  Good: { color: 'text-cyan-500 dark:text-cyan-400', Icon: ShieldCheckIcon },
  Fair: { color: 'text-yellow-500 dark:text-yellow-400', Icon: ShieldExclamationIcon },
  Poor: { color: 'text-red-500 dark:text-red-400', Icon: ShieldExclamationIcon },
};

const AnalysisCategory: React.FC<{
  Icon: React.FC<any>;
  title: string;
  rating: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  description: string;
  activities?: string[];
  children?: React.ReactNode;
}> = ({ Icon, title, rating, description, activities, children }) => {
  const ratingInfo = ratingMap[rating] || ratingMap.Poor;
  return (
    <div className="bg-gray-100 dark:bg-slate-700/50 p-4 rounded-lg">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-6 h-6 text-slate-500 dark:text-slate-400" />
        <h4 className="font-bold text-lg text-slate-800 dark:text-slate-200">{title}</h4>
        <div className={`ml-auto flex items-center gap-1.5 text-sm font-semibold ${ratingInfo.color}`}>
          <ratingInfo.Icon className="w-4 h-4" />
          <span>{rating}</span>
        </div>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{description}</p>
      {activities && (
        <div>
          <h5 className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500 mb-1.5">Good For</h5>
          <div className="flex flex-wrap gap-2">
            {activities.map(activity => (
              <span key={activity} className="text-xs font-medium bg-cyan-100 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-300 px-2 py-1 rounded-full">
                {activity}
              </span>
            ))}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

interface AIAnalysisModalProps {
    isOpen: boolean;
    onClose: () => void;
    analysis: AIAnalysisResult | null;
    isLoading: boolean;
    error: string | null;
}

const AIAnalysisModal: React.FC<AIAnalysisModalProps> = ({ isOpen, onClose, analysis, isLoading, error }) => {
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-analysis-title"
            style={{ animation: 'modal-fade-in 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
        >
            <div
                className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: 'modal-content-pop 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
            >
                {/* Header */}
                <div className="flex-shrink-0 p-6 pb-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <SparklesIcon className="w-6 h-6 text-purple-500 dark:text-purple-400" />
                            <h3 id="ai-analysis-title" className="text-xl font-bold text-slate-800 dark:text-slate-100">AI Insights</h3>
                        </div>
                        <button onClick={onClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-3xl leading-none" aria-label="Close modal">&times;</button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto px-6 pb-6">
                    {isLoading && (
                        <div className="space-y-3 animate-pulse">
                            <div className="h-5 bg-gray-300 dark:bg-slate-700 rounded w-3/4"></div>
                            <div className="h-4 bg-gray-300 dark:bg-slate-700 rounded w-full mt-4"></div>
                            <div className="h-4 bg-gray-300 dark:bg-slate-700 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-300 dark:bg-slate-700 rounded w-full mt-4"></div>
                            <div className="h-4 bg-gray-300 dark:bg-slate-700 rounded w-4/6"></div>
                        </div>
                    )}
                    
                    {error && (
                        <p className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-3 rounded-md">{error}</p>
                    )}

                    {analysis && !isLoading && (
                        <div className="space-y-4">
                            <p className="text-center text-lg font-semibold text-slate-700 dark:text-slate-200 p-3 bg-gray-100 dark:bg-slate-700/50 rounded-md">
                                "{analysis.summary}"
                            </p>
                            
                            <AnalysisCategory
                                Icon={DownloadIcon}
                                title="Download"
                                rating={analysis.download.rating}
                                description={analysis.download.description}
                                activities={analysis.download.activities}
                            />
                            
                            <AnalysisCategory
                                Icon={UploadIcon}
                                title="Upload"
                                rating={analysis.upload.rating}
                                description={analysis.upload.description}
                                activities={analysis.upload.activities}
                            />
                            
                            <AnalysisCategory
                                Icon={PingIcon}
                                title="Latency"
                                rating={analysis.latency.rating}
                                description={analysis.latency.pingDescription}
                            >
                                <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{analysis.latency.jitterDescription}</p>
                            </AnalysisCategory>

                            {analysis.historyTrend && (
                                 <div className="flex items-start gap-3 p-3 text-sm text-slate-600 dark:text-slate-400 bg-gray-200/50 dark:bg-slate-700/50 rounded-lg">
                                    <InfoIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                    <p><strong className="text-slate-700 dark:text-slate-300">Trend:</strong> {analysis.historyTrend}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AIAnalysisModal;